import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnMenuFilter,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnMenu } from '../components/columnMenu';
import { ActionCell, ActionsCell } from '../components/grid/actions-cell';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { NoRecords } from '../components/grid/no-records';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import {
  copyAssessmentToLibrary,
  getGlobalAssessmentById,
  getGlobalContentByAssessmentIdAsync,
  getGlobalDocumentLatestAsync
} from '../services/assessment';
import { appStore } from '../stores/app-store';
import { Assessment } from '../types/assessment';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { GlobalAssessmentCardView } from './global-assessment-cardview';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import merge from 'lodash.merge';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { useToast } from '../contexts/toast-context';
import { useGetAssessments } from '../hooks/services/use-assessments';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const GlobalAssessmentGrid = () => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetAssessments(
    toDataSourceRequestString(dataState),
    'library'
  );

  const handleTitleClick = (id: number) => {
    navigate(`../global-assessment-details/${id}`);
  };

  const onColumnTitleClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer p-0 text-start'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const onActionRow = (props: GridCellProps) => {
    return (
      <>
        <ActionsCell {...props}>
          <ActionCell
            title={'View assessment'}
            icon={'visibility'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => handleTitleClick(props.dataItem.id)}
          />
          <ActionCell
            title={'Copy assessment'}
            icon={'content_copy'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onCopyAssessment(props.dataItem)}
          />
        </ActionsCell>
      </>
    );
  };

  const onCopyAssessment = async (assessments: Assessment) => {
    try {
      appStore.showLoading();

      const document = await getGlobalDocumentLatestAsync(assessments.id);

      // combine assessment and assessment documents (resources)
      let assess1 = await getGlobalAssessmentById(assessments.id);
      assess1.source = 'Imported';
      let assess2 = await getGlobalContentByAssessmentIdAsync(
        assessments.id,
        document.id
      );

      let mergedAssessment = merge(assess1, assess2);

      await copyAssessmentToLibrary(mergedAssessment);

      onToastOpen({
        title: 'Assessment List',
        body: <div>Assessment copied to Assessment list.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Assessment List',
        body: <div>Failed to copy to Assessment list.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      mutate();
    }
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.createdDateUtc)}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps, field: string) => {
    return (
      <td title={props.dataItem[field]} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>{props.dataItem[field]}</div>
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={`${' Global Assessment'}`}>
        <HelpSupport
          title="Global assessment library guide"
          url="https://forum.catalystglobal.com/t/4273"
        />
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search assessment'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`assessment.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-assessment'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'name'}
                title={'Name'}
                columnMenu={ColumnMenu}
                cells={{
                  data: onColumnTitleClick
                }}
              />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                width={110}
                cells={{
                  data: DateCell
                }}
              />
              <Column
                field={'version'}
                title={'Version'}
                width={120}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'language'}
                title={'Languages'}
                width={200}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'language')
                }}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              <Column
                field={'organisation'}
                title={'Organisation Name'}
                columnMenu={ColumnMenu}
                width={205}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'organisation')
                }}
              />
              <Column
                title={'Actions'}
                cells={{ data: onActionRow }}
                width={120}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}

          {gridMode === 'card' && (
            <GlobalAssessmentCardView
              data={data?.data ?? []}
              onTitlelick={handleTitleClick}
              onCopy={onCopyAssessment}
              onView={handleTitleClick}
              total={data?.total}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>
      </div>
    </>
  );
};
export default GlobalAssessmentGrid;
